<template>
	<v-row align="start" align-content="start" justify="start">
		<v-overlay :model-value="loading" class="align-center justify-center">
			<v-progress-circular indeterminate size="64" color="primary" />
		</v-overlay>

		<!-- DCTL-1443: flag on -> new per-project SDE dashboard; flag off ->
		     the existing global queue (unchanged escape hatch). -->
		<template v-if="!loading">
			<v-col v-if="projectDashboardEnabled" cols="12" order="1">
				<!-- DCTL-2113: rollout announcement. Only while the opt-out is still
				     available (showLegacyToggle); dismissal is remembered locally. -->
				<!-- DCTL-2283: opaque tint + ink rather than variant="tonal", whose
				     alpha wash composites differently per surface (COLOR.md rule 4).
				     icon= is explicit because dropping type= also drops the icon
				     Vuetify derived from it. The border="start" accent bar reads
				     currentColor through the pinned mask in
				     scss/border-opacity-masks.scss, so it now tracks the ink. -->
				<v-alert
					v-if="showLegacyToggle && !announcementDismissed"
					icon="mdi-information"
					border="start"
					class="mb-4 bg-valence-info-tint text-valence-info-ink"
					closable
					@click:close="dismissAnnouncement"
				>
					<div class="text-body-2 text-grey-darken-3">
						The per-project dashboard is now the default. You can switch back to
						the classic queue temporarily from the menu on the left while we
						finish the rollout.
					</div>
				</v-alert>

				<sde-project-dashboard />
			</v-col>

			<v-col v-else-if="displayFullPaperTable" cols="12" order="1">
				<paper-table admin-queue-mode />
			</v-col>

			<v-col v-else cols="12" order="1">
				<paper-table sde-queue-mode></paper-table>
			</v-col>
		</template>
	</v-row>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import { useProjectDashboardEnabled } from "@/composables/useProjectDashboardEnabled";
import PaperTable from "@/pages/PaperManager/PaperTable";
import SdeProjectDashboard from "@/pages/SdeProjectDashboard";

// DCTL-2113: localStorage key for the one-time rollout announcement dismissal.
const ANNOUNCEMENT_DISMISS_KEY = "dactyl.projectDashboardAnnouncementDismissed";

export default {
	name: "DactylDashboard",

	components: {
		PaperTable,
		SdeProjectDashboard,
	},

	setup() {
		// DCTL-2113: honor the per-user classic-queue opt-out, not just the raw flag.
		const { enabled: projectDashboardEnabled, showLegacyToggle } =
			useProjectDashboardEnabled();
		return { projectDashboardEnabled, showLegacyToggle };
	},

	data() {
		return {
			displayFullPaperTable: false,
			loading: false,
			// DCTL-2113: rollout announcement dismissal, remembered in localStorage.
			announcementDismissed:
				localStorage.getItem(ANNOUNCEMENT_DISMISS_KEY) === "true",
		};
	},

	computed: {
		paper() {
			return useMainStore().paper;
		},
		saveUUID() {
			return useMainStore().saveUUID;
		},
		user() {
			return useMainStore().user;
		},
	},

	created() {
		this.displayFullPaperTable = useMainStore().can("access_work_queues");
		this.clear();
	},

	methods: {
		...mapActions(useMainStore, [
			"clearPaper",
			"clearSaveUUID",
			"setPaper",
			"setSaveUUID",
		]),
		clear() {
			this.clearPaper();
			this.clearSaveUUID();
		},
		dismissAnnouncement() {
			this.announcementDismissed = true;
			localStorage.setItem(ANNOUNCEMENT_DISMISS_KEY, "true");
		},
	},
};
</script>

<style scoped lang="scss">
.col {
	flex-grow: 0;
}
</style>
